import React, { useState } from 'react';
import { Box, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { ARE_YOU_SURE, CREATE_REQUESTS, SEND_REQUESTS, USE_COMPANY_TEMPLATE_TOOLTIP } from 'common/constants/firms';
import { NO_STRING, YES_STRING } from 'common/constants/general';
import CustomPrimaryButton from 'components/CustomPrimaryButton/CustomPrimaryButton';
import { useCreateRequestedTasks } from 'services/hooks/firm';
import { CompanyDataWithPermissions, FRModalActionButtonProps } from './types';
import { SCALAR_IO } from '../../constants';

const FRModalActionButton: React.FC<FRModalActionButtonProps> = ({
  selectedCompanies,
  setSelectedCompanies,
  handleClose,
  firmId,
  documents,
  questions,
  sendToAllCompanies,
  userId,
  useCompanyTemplates,
  setUseCompanyTemplates,
}) => {
  const [needToConfirm, setNeedToConfirm] = useState(false);
  const [createRequestedTasks] = useCreateRequestedTasks();

  const handleConfirm = () => {
    setNeedToConfirm(true);
  };

  const handleCancel = () => {
    setNeedToConfirm(false);
  };

  const getResponsibleUsersIds = (company: CompanyDataWithPermissions) => {
    if (company.onlyStaffEmails) {
      return (
        company.users_with_company_permissions?.filter(user => user.email.endsWith(SCALAR_IO)).map(user => user.id) || [
          userId,
        ]
      );
    }
    return company.users_with_company_permissions?.map(user => user.id) || [userId];
  };

  const handleSend = () => {
    // 1. Prepare request data
    const tmpQuestions = questions.map(q => q.id);
    const tmpDocuments = documents.map(d => d.id);

    // After completing the feature: User Management, delete tmpSelectedCompanies and use directly selectedCompanies. We are hardcoding this until we have the User Management feature.
    const tmpSelectedCompanies = selectedCompanies.map(company => ({
      ...company,
      responsible_users: getResponsibleUsersIds(company),
    }));

    const requestData = {
      firm: firmId,
      questions: tmpQuestions,
      requested_documents: tmpDocuments,
      send_email: !sendToAllCompanies,
      companies: tmpSelectedCompanies,
      assign_all_to_user: sendToAllCompanies,
      use_company_template: useCompanyTemplates,
    };
    // 2. Send request
    createRequestedTasks(requestData);
    // 3. Close modal
    handleClose();
    // 4. Scroll to top of the page (to see the loading line)
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    // 5. Reset state
    setSelectedCompanies([]);
  };

  if (!needToConfirm) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{
          width: '100%',
        }}>
        <Tooltip placement="top" title={USE_COMPANY_TEMPLATE_TOOLTIP}>
          <FormControlLabel
            control={
              <Switch checked={useCompanyTemplates} onChange={() => setUseCompanyTemplates(current => !current)} />
            }
            label="Use company template"
          />
        </Tooltip>
        <CustomPrimaryButton onClick={handleConfirm} disabled={isEmpty(selectedCompanies)}>
          {sendToAllCompanies ? CREATE_REQUESTS : SEND_REQUESTS}
        </CustomPrimaryButton>
      </Box>
    );
  }

  if (needToConfirm) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{
          width: '100%',
        }}>
        <Typography
          color="primary"
          style={{
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
          gutterBottom>
          {ARE_YOU_SURE}
        </Typography>
        <Box display="flex" justifyContent="center">
          <Box marginRight="1rem">
            <CustomPrimaryButton onClick={handleCancel} variant="outlined">
              {NO_STRING}
            </CustomPrimaryButton>
          </Box>
          <CustomPrimaryButton onClick={handleSend}>{YES_STRING}</CustomPrimaryButton>
        </Box>
      </Box>
    );
  }

  return null;
};

export default FRModalActionButton;
